/**
 * AI API — klasik editor generation ve quote/iptal uçları.
 *
 * Kapsam: `quote`, `stop-stream`, `query` ve `query-stream`. Eski
 * `conversation/*` chat uçları Faz 6 / T6.1 ile kaldırıldı; aktif chat akışı
 * `AgentApi.chat` (`/api/agent/chat`), dosya yaşam döngüsü ise
 * `ProjectDocumentApi` üzerinden yürür.
 *
 * Not: `generateWithAIAsStream` ReadableStream kullandığı için factory'ye
 * taşınmadı; auth header'ı `getHeaders()` ile elle ekleniyor.
 */
import {createApi} from './_factory'
import {catchError} from '@shared/http-client'
import {getHeaders} from '@shared/helpers/get-headers.helper'
import {cookies} from '@shared/helpers'
import {config} from '@/config'
import type {BoardsNs} from '@shared/types/boards.namespace'
import {Observable} from 'rxjs'

const api = createApi('v0/ai')

export const AiApi = {
  // — Quote / iptal —
  getAIBalance: (workspaceId: string) =>
    api.get<number>(`quote/${workspaceId}`),

  cancelStream: () => {
    const streamId = cookies.get('X-Stream-Id')
    return api.delete(`stop-stream/${streamId}`)
  },

  // — Klasik query —
  generateWithAI: (data: BoardsNs.AISearchRequest) =>
    api.post<BoardsNs.AISearchRequest, BoardsNs.AISearchResult>('query', data),

  // — SSE-benzeri streaming (fetch + ReadableStream) —
  generateWithAIAsStream: (data: BoardsNs.AISearchRequest): Observable<string> => {
    const url = `${config('APP_API_URL')}/v0/ai/query-stream`
    const headers = {...getHeaders(), 'Content-Type': 'application/json'}

    return new Observable<string>((observer) => {
      fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      })
        .then(async (response) => {
          if (!response.ok) {
            const errorBody = await response.json()
            catchError({response: {status: response.status, data: errorBody}}, true)
            throw new Error(`HTTP error! status: ${response.status}`)
          }
          const reader = response.body?.getReader()
          if (!reader) throw new Error('ReadableStream not supported.')

          const decoder = new TextDecoder()
          const readStream = (): void => {
            reader
              .read()
              .then(({done, value}) => {
                if (done) {
                  observer.complete()
                  return
                }
                observer.next(decoder.decode(value, {stream: true}))
                readStream()
              })
              .catch((err) => observer.error(err))
          }
          readStream()
        })
        .catch((error) => observer.error(error))
    })
  },
}
